if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      atcDedupeObserver = undefined;
      atcDedupeObserverTimer = undefined;
      nativeFallbackClickHandler = undefined;
      tCustomizerFallbackClickHandler = undefined;
      drawerFallbackTimers = [];
      miniCartOpenInFlight = false;
      miniCartOpenLockUntil = 0;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      // DMIXER-VARIANT-TIERS: START — Synchronize offers based on campaign activation.
      isVariantTierCampaignActive() {
        return document.documentElement.hasAttribute(
          'data-dmixer-variant-tier-campaign-active',
        );
      }

      syncVariantTierOffersVisibility() {
        const campaignActive = this.isVariantTierCampaignActive();

        document.documentElement.toggleAttribute(
          'data-dmixer-variant-tier-campaign-active',
          campaignActive,
        );

        const offers = this.querySelector(
          `#VariantTiers-${this.dataset.section}`,
        );

        if (!offers) return;

        const hasOffers =
          offers.querySelector('.variant-tier-offers__list') !== null;

        offers.classList.toggle(
          'hidden',
          !hasOffers || !campaignActive,
        );
      }
      // DMIXER-VARIANT-TIERS: END

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        // Variant changes run dedupeThirdPartyAtcButtons() (which installs the
        // fallback handlers that refresh/open the mini cart after a qikify/
        // TCustomizer add-to-cart). On initial page load that never ran, so for
        // products whose custom options are already shown for the default
        // selection — and for products with no variants at all, where the
        // variant-change event never fires — the mini cart was left un-refreshed.
        // Run it once on load so the fallback handlers are wired up immediately.
        this.dedupeThirdPartyAtcButtons();

        // DMIXER-VARIANT-TIERS: START — Synchronize offers based on campaign activation.
        const syncTierOffers = () => this.syncVariantTierOffersVisibility();

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', syncTierOffers, {
            once: true,
          });
        } else {
          syncTierOffers();
        }
        // DMIXER-VARIANT-TIERS: END
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.teardownAtcDedupeObserver();
        this.teardownNativeFallbackHandler();
        this.teardownTCustomizerFallbackHandler();
        this.clearDrawerFallbackTimers();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              return;
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          if (!variant) {
            // DMIXER-VARIANT-TIERS: START — Synchronize offers after variant changes.
            updateSourceFromDestination('VariantTiers');
            this.syncVariantTierOffersVisibility();
            // DMIXER-VARIANT-TIERS: END
            this.updateStockStatus(html);
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          updateSourceFromDestination('price');
          updateSourceFromDestination('Uom', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          // Also sync visibility-hidden class for SKU (used when variant has no SKU)
          const skuSource = html.getElementById(`Sku-${this.sectionId}`);
          const skuDestination = this.querySelector(`#Sku-${this.dataset.section}`);
          if (skuSource && skuDestination) {
            skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
          }
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          // DMIXER-VARIANT-TIERS: START — Synchronize offers after variant changes.
          updateSourceFromDestination('VariantTiers');
          this.syncVariantTierOffersVisibility();
          // DMIXER-VARIANT-TIERS: END

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          this.dedupeThirdPartyAtcButtons();
        };
      }

      dedupeThirdPartyAtcButtons() {
        const runDedupe = () => {
          const sectionKey = this.dataset.section || this.sectionId;
          const appRoots = this.querySelectorAll(
            '[id*="qikify"], [class*="qikify"], [data-app*="qikify"], [data-shopify-app-block*="qikify"]'
          );

          appRoots.forEach((root) => {
            const atcCandidates = Array.from(
              root.querySelectorAll('button, a, [role="button"], input[type="submit"]')
            ).filter((el) => {
              const text = (el.textContent || el.value || '').trim().toLowerCase();
              const cls = (el.className || '').toString().toLowerCase();
              const name = (el.getAttribute('name') || '').toLowerCase();

              return (
                text.includes('add to cart') ||
                cls.includes('add-to-cart') ||
                cls.includes('add_to_cart') ||
                cls.includes('atc') ||
                name === 'add'
              );
            });

            if (atcCandidates.length <= 1) return;
            atcCandidates.slice(1).forEach((el) => {
              el.remove();
            });
          });

          // Some apps clone the native Dawn buy-buttons block on variant changes.
          // Keep only one native submit for this section.
          const nativeSubmitSelector = `[id="ProductSubmitButton-${sectionKey}"]`;
          const nativeSubmits = Array.from(this.querySelectorAll(nativeSubmitSelector));
          if (nativeSubmits.length > 1) {
            nativeSubmits.slice(1).forEach((btn) => {
              const formRoot = btn.closest('product-form') || btn.closest('.product-form') || btn;
              formRoot.remove();
            });
          }

          // Extra safety: if multiple product-form wrappers still exist, keep the one
          // inside the intended button column and remove additional clones.
          const allProductForms = Array.from(this.querySelectorAll('product-form.product-form'));
          const keepForm =
            this.querySelector('.product__btn-col product-form.product-form') ||
            this.querySelector('.product__btn-col product-form') ||
            allProductForms[0];
          if (allProductForms.length > 1 && keepForm) {
            allProductForms.forEach((formEl) => {
              if (formEl === keepForm) return;
              formEl.remove();
            });
          }

          // TCustomizer overlap button duplication: keep a single overlap button.
          const tcButtons = Array.from(
            this.querySelectorAll(
              '.product__btn-col .tcustomizer-overlap-button, .product__btn-col #tcustomizer-add'
            )
          );
          if (tcButtons.length > 1) {
            tcButtons.slice(1).forEach((btn) => {
              btn.remove();
            });
          }

          // Fallback: when a variant has no custom option fields, use native submit.
          const activeForm =
            this.querySelector(`form#product-form-${sectionKey}`) ||
            this.querySelector('.product__btn-col form[data-type="add-to-cart-form"]');
          const nativeSubmit = this.querySelector(`#ProductSubmitButton-${sectionKey}`);
          const tcPrimary =
            this.querySelector('.product__btn-col .tcustomizer-overlap-button') ||
            this.querySelector('.product__btn-col #tcustomizer-add');

          if (activeForm && nativeSubmit) {
            const isVariantSoldOut =
              nativeSubmit.hasAttribute('disabled') || nativeSubmit.getAttribute('aria-disabled') === 'true';

            if (isVariantSoldOut) {
              if (tcPrimary) {
                tcPrimary.setAttribute('aria-disabled', 'true');
                tcPrimary.setAttribute('disabled', 'disabled');
              }
              this.teardownNativeFallbackHandler();
              this.teardownTCustomizerFallbackHandler();
              return;
            } else if (tcPrimary) {
              tcPrimary.removeAttribute('aria-disabled');
              tcPrimary.removeAttribute('disabled');
            }

            const shouldUseNativeFallback =
              !tcPrimary ||
              tcPrimary.disabled ||
              tcPrimary.getAttribute('aria-disabled') === 'true';

            if (shouldUseNativeFallback) {
              nativeSubmit.classList.remove('tcustomizer-hide-button');
              nativeSubmit.style.removeProperty('display');
              // Clear stale disabled state that can be left by app scripts.
              nativeSubmit.removeAttribute('aria-disabled');
              nativeSubmit.removeAttribute('disabled');
              this.setupNativeFallbackHandler(activeForm, nativeSubmit);
              this.teardownTCustomizerFallbackHandler();

              if (tcPrimary) {
                tcPrimary.remove();
              }
            } else {
              this.teardownNativeFallbackHandler();
              this.setupTCustomizerFallbackHandler(activeForm, nativeSubmit, tcPrimary);
            }
          }
        };

        runDedupe();
        // Some app scripts append buttons asynchronously after variant events.
        window.setTimeout(runDedupe, 150);
        window.setTimeout(runDedupe, 500);
        window.setTimeout(runDedupe, 1000);
        this.setupAtcDedupeObserver(runDedupe);
      }

      setupAtcDedupeObserver(runDedupe) {
        this.teardownAtcDedupeObserver();
        this.atcDedupeObserver = new MutationObserver((mutations) => {
          const hasAddedNodes = mutations.some((m) => m.addedNodes && m.addedNodes.length > 0);
          if (!hasAddedNodes) return;
          runDedupe();
        });
        this.atcDedupeObserver.observe(this, { childList: true, subtree: true });
        this.atcDedupeObserverTimer = window.setTimeout(() => this.teardownAtcDedupeObserver(), 3000);
      }

      teardownAtcDedupeObserver() {
        if (this.atcDedupeObserver) {
          this.atcDedupeObserver.disconnect();
          this.atcDedupeObserver = undefined;
        }
        if (this.atcDedupeObserverTimer) {
          window.clearTimeout(this.atcDedupeObserverTimer);
          this.atcDedupeObserverTimer = undefined;
        }
      }

      setupNativeFallbackHandler(activeForm, nativeSubmit) {
        this.teardownNativeFallbackHandler();
        this.nativeFallbackClickHandler = (evt) => {
          if (nativeSubmit.hasAttribute('disabled') || nativeSubmit.getAttribute('aria-disabled') === 'true') {
            evt.preventDefault();
            evt.stopImmediatePropagation();
            return;
          }
          evt.preventDefault();
          evt.stopImmediatePropagation();
          nativeSubmit.removeAttribute('aria-disabled');
          nativeSubmit.removeAttribute('disabled');
          activeForm.requestSubmit(nativeSubmit);
        };
        nativeSubmit.addEventListener('click', this.nativeFallbackClickHandler, true);
      }

      teardownNativeFallbackHandler() {
        if (!this.nativeFallbackClickHandler) return;
        const sectionKey = this.dataset.section || this.sectionId;
        const nativeSubmit = this.querySelector(`#ProductSubmitButton-${sectionKey}`);
        if (nativeSubmit) nativeSubmit.removeEventListener('click', this.nativeFallbackClickHandler, true);
        this.nativeFallbackClickHandler = undefined;
      }

      setupTCustomizerFallbackHandler(activeForm, nativeSubmit, tcPrimary) {
        this.teardownTCustomizerFallbackHandler();
        if (!tcPrimary) return;
        this.tCustomizerFallbackClickHandler = (evt) => {
          if (
            nativeSubmit.hasAttribute('disabled') ||
            nativeSubmit.getAttribute('aria-disabled') === 'true' ||
            tcPrimary.hasAttribute('disabled') ||
            tcPrimary.getAttribute('aria-disabled') === 'true'
          ) {
            evt.preventDefault();
            evt.stopImmediatePropagation();
            return;
          }

          const customPropertyFields = Array.from(
            this.querySelectorAll(
              'input[name^="properties["], select[name^="properties["], textarea[name^="properties["]'
            )
          ).filter((field) => {
            const name = field.getAttribute('name') || '';
            if (name === 'properties[_shq_cart_id]') return false;
            if (field.closest('template')) return false;
            if (field.disabled) return false;
            // Count only fields that are actually active/visible in current UI state.
            if (field.offsetParent === null && field.getAttribute('type') !== 'hidden') return false;
            return true;
          });

          // If there are no real custom option fields for this variant, bypass
          // TCustomizer overlap flow and submit through native product form.
          if (customPropertyFields.length === 0) {
            evt.preventDefault();
            evt.stopImmediatePropagation();
            // Keep native submit visually hidden to avoid showing a duplicate CTA.
            // We only use it as the actual submit target.
            nativeSubmit.removeAttribute('aria-disabled');
            nativeSubmit.removeAttribute('disabled');
            tcPrimary.setAttribute('aria-disabled', 'true');
            tcPrimary.setAttribute('disabled', 'disabled');
            activeForm.requestSubmit(nativeSubmit);
            window.setTimeout(() => {
              tcPrimary.removeAttribute('aria-disabled');
              tcPrimary.removeAttribute('disabled');
            }, 1200);
            return;
          }

          // Customizer flow: app handles add-to-cart asynchronously, so open drawer
          // after cart count changes.
          this.waitForCartIncreaseAndOpenDrawer();
          this.scheduleDrawerOpenFallback();
        };
        tcPrimary.addEventListener('click', this.tCustomizerFallbackClickHandler, true);
      }

      fetchCartItemCount() {
        return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => cart?.item_count || 0)
          .catch(() => null);
      }

      waitForCartIncreaseAndOpenDrawer() {
        this.fetchCartItemCount().then((beforeCount) => {
          if (beforeCount === null) return;
          let attempts = 0;
          const maxAttempts = 8;
          const intervalMs = 350;
          const poll = () => {
            attempts += 1;
            this.fetchCartItemCount().then((currentCount) => {
              if (currentCount !== null && currentCount > beforeCount) {
                this.clearDrawerFallbackTimers();
                this.openMiniCartDrawer();
                return;
              }
              if (attempts < maxAttempts) window.setTimeout(poll, intervalMs);
            });
          };
          window.setTimeout(poll, intervalMs);
        });
      }

      scheduleDrawerOpenFallback() {
        // TCustomizer can update cart through paths that don't immediately reflect
        // in cart.js polling. Run delayed fallback opens to keep UX consistent.
        this.clearDrawerFallbackTimers();
        this.drawerFallbackTimers.push(window.setTimeout(() => this.openMiniCartDrawer(), 1200));
        this.drawerFallbackTimers.push(window.setTimeout(() => this.openMiniCartDrawer(), 2200));
      }

      clearDrawerFallbackTimers() {
        if (!this.drawerFallbackTimers.length) return;
        this.drawerFallbackTimers.forEach((timerId) => window.clearTimeout(timerId));
        this.drawerFallbackTimers = [];
      }

      refreshMiniCartDrawer() {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return Promise.resolve();
        return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
          .then((r) => r.json())
          .then((sections) => {
            const drawerHtml = sections?.['cart-drawer'];
            const bubbleHtml = sections?.['cart-icon-bubble'];
            if (!drawerHtml) return;
            const parser = new DOMParser();
            const drawerDoc = parser.parseFromString(drawerHtml, 'text/html');
            const refreshedDrawer = drawerDoc.querySelector('cart-drawer');
            if (refreshedDrawer) {
              cartDrawer.className = refreshedDrawer.className;
              cartDrawer.innerHTML = refreshedDrawer.innerHTML;
            }
            if (cartDrawer.classList.contains('is-empty')) {
              cartDrawer.classList.remove('is-empty');
            }
            if (bubbleHtml) {
              const bubbleDoc = parser.parseFromString(bubbleHtml, 'text/html');
              const sectionInner = bubbleDoc.querySelector('.shopify-section');
              const liveBubble = document.querySelector('#cart-icon-bubble');
              if (sectionInner && liveBubble) liveBubble.innerHTML = sectionInner.innerHTML;
            }
          })
          .catch(() => {});
      }

      openMiniCartDrawer() {
        const now = Date.now();
        if (this.miniCartOpenInFlight || now < this.miniCartOpenLockUntil) return;
        this.miniCartOpenInFlight = true;
        this.miniCartOpenLockUntil = now + 2500;

        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) {
          this.miniCartOpenInFlight = false;
          return;
        }
        this.refreshMiniCartDrawer().finally(() => {
          if (typeof cartDrawer.open === 'function') {
            try {
              cartDrawer.open();
            } catch (_err) {}
          }
          document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
          window.dispatchEvent(new CustomEvent('cart-drawer:open'));
          window.setTimeout(() => {
            this.miniCartOpenInFlight = false;
          }, 250);
        });
      }

      teardownTCustomizerFallbackHandler() {
        if (!this.tCustomizerFallbackClickHandler) return;
        const tcPrimary =
          this.querySelector('.product__btn-col .tcustomizer-overlap-button') ||
          this.querySelector('.product__btn-col #tcustomizer-add');
        if (tcPrimary) tcPrimary.removeEventListener('click', this.tCustomizerFallbackClickHandler, true);
        this.tCustomizerFallbackClickHandler = undefined;
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateStockStatus(html) {
        const source = html.querySelector('.stock-status-wrapper[data-context="pdp"]');
        const destination = this.querySelector('.stock-status-wrapper[data-context="pdp"]');
        if (source && destination) {
          destination.innerHTML = source.innerHTML;
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Uom', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
